import { Modal } from 'antd';
import { parseImportError, IMPORT_ERROR_TYPES } from '../utils/importErrorHandler';

/**
 * Hook para tratar erros de importação de forma estruturada.
 * Erros que exigem modal declarativo retornam payload; o pai renderiza o modal com visible/onOk.
 */
export const useImportErrorHandler = () => {
  /**
   * Trata erro de duplicidade de cooperados
   * Retorna objeto com type e lista para o pai exibir ModalDuplicateCooperators
   */
  const handleDuplicateCooperators = (duplicates) => {
    const duplicatesList = Array.isArray(duplicates) ? duplicates : [];
    return {
      type: IMPORT_ERROR_TYPES.DUPLICATE_COOPERATORS,
      cooperatorsList: duplicatesList,
    };
  };

  /**
   * Trata erro de colunas divergentes
   * Retorna objeto com type e campos para tratamento posterior
   */
  const handleMissingColumns = (missingColumns, file, sheetIndex) => {
    const columnsList = Array.isArray(missingColumns) ? missingColumns : [];
    return {
      type: IMPORT_ERROR_TYPES.MISSING_COLUMNS,
      missingFields: columnsList.join('|'),
      file,
      sheetIndex,
    };
  };

  /**
   * Trata erro de ajuda de custo presente
   */
  const handleCostAllowance = (message) => {
    Modal.error({
      title: 'A planilha possui ajuda de custo',
      content: message || 'Impossível utilizar verba "Ajuda de Custo". Excluir a coluna para seguir com a importação.',
    });
    return null;
  };

  /**
   * Trata erro genérico (sem cooperadores, etc)
   */
  const handleGenericError = (message) => {
    Modal.error({
      title: 'Erro na importação',
      content: message || 'Ocorreu um erro ao importar a planilha. Por favor, tente novamente.',
    });
    return null;
  };

  /**
   * Função principal para tratar erros de importação
   * @param {Error} error - Erro do axios
   * @param {File} file - Arquivo sendo importado (opcional)
   * @param {number} sheetIndex - Índice da aba (opcional)
   * @returns {Promise|Object|null} - Retorna Promise para erros que requerem confirmação, objeto para erros que requerem tratamento especial, ou null
   */
  const handleImportError = async (error, file = null, sheetIndex = null) => {
    const parsedError = parseImportError(error);

    switch (parsedError.type) {
      case IMPORT_ERROR_TYPES.HIRING_MODEL_MISMATCH:
        return {
          type: IMPORT_ERROR_TYPES.HIRING_MODEL_MISMATCH,
          mismatches: parsedError.details?.mismatches || [],
        };

      case IMPORT_ERROR_TYPES.SHEET_SCREEN_MISMATCH:
        return {
          type: IMPORT_ERROR_TYPES.SHEET_SCREEN_MISMATCH,
          cooperatorsList: [
            ...(parsedError.details?.inSheetNotOnScreen || []),
            ...(parsedError.details?.inScreenNotInSheet || []),
          ],
        };

      case IMPORT_ERROR_TYPES.DUPLICATE_COOPERATORS:
        return handleDuplicateCooperators(parsedError.details?.duplicates || []);

      case IMPORT_ERROR_TYPES.MISSING_COLUMNS:
        return handleMissingColumns(parsedError.details?.missingColumns || [], file, sheetIndex);

      case IMPORT_ERROR_TYPES.COST_ALLOWANCE_PRESENT:
        return handleCostAllowance(parsedError.message);

      case IMPORT_ERROR_TYPES.NO_COOPERATORS:
      case IMPORT_ERROR_TYPES.UNKNOWN:
      default:
        return handleGenericError(parsedError.message);
    }
  };

  return {
    handleImportError,
    handleDuplicateCooperators,
    handleMissingColumns,
    handleCostAllowance,
    handleGenericError,
  };
};
